import { Component, OnInit } from "@angular/core";
import { AutotextService } from "src/app/autotext.service";
import { ExamsService } from "src/app/exams.service";
import { GastroFormsService } from "src/app/gastro-forms.service";
import { UsgService } from "src/app/usg.service";
import { LoginService } from "src/app/login.service";
import { MedicationService } from "src/app/medication.service";
import { PatientsService } from "src/app/patients.service";
import { AngularEditorConfig } from "@kolkov/angular-editor";

@Component({
    selector: "app-ussummary",
    templateUrl: "./ussummary.component.html",
    styleUrls: ["./ussummary.component.scss"],
    standalone: false
})
export class UssummaryComponent implements OnInit {
  editorConfig: AngularEditorConfig = {
    editable: true,
    height: "fit-contnet",
    toolbarHiddenButtons: [
      ["subscript", "superscript", "strikeThrough"],
      [
        "backgroundColor",
        "customClasses",
        "link",
        "unlink",
        "insertImage",
        "insertVideo",
        "removeFormat",
        "toggleEditorMode",
      ],
    ],
  };

  probeTypeDropdownList: string[] = [];
  extraProbeType: string = '';
  loadingProbeTypes = false;

  refDocList: string[] = [];
  newRefDoc: string = '';
  loadingRefDocs = false;

  constructor(
    public ls: LoginService,
    public gs: UsgService,
    public es: ExamsService,
    public atS: AutotextService,
    public medS: MedicationService,
    public gfs: GastroFormsService,
    private patService: PatientsService
  ) {}

  // AR-24 change 3: AI medical-history summary from the patient's answered questionnaire.
  aiSummaryLoading = false;
  getAiSummary() {
    if (this.aiSummaryLoading) return;
    this.aiSummaryLoading = true;
    this.es.getAiSummary(this.es.ExamType, this.patService.getPat()?.PID).subscribe(
      (res: any) => {
        this.aiSummaryLoading = false;
        if (res && res.summary) {
          const control = this.gs.gastroForm?.get('MedicalHistory');
          if (control) {
            const existing = (control.value || '').toString().trim();
            const previous = (res.previousSummary || '').toString().trim();
            // Replace only our own previously-pasted AI block (exact match), so a
            // doctor's hand-typed notes are never overwritten; otherwise append.
            if (previous && existing === previous) {
              control.setValue(res.summary);
            } else {
              control.setValue(existing ? existing + '\n\n' + res.summary : res.summary);
            }
          }
        } else if (res && res.reason) {
          alert('Could not generate a summary (' + res.reason + ').');
        }
      },
      () => {
        this.aiSummaryLoading = false;
      }
    );
  }

  loadRefDocs() {
    const clinicId = this.ls.GID || this.ls.ClinicID;
    this.loadingRefDocs = true;
    this.gs.getDropdownOptions(clinicId, 'ReffDoc').subscribe({
      next: (options: string[]) => {
        this.refDocList = options || [];
        const current = this.gs.gastroForm.get('ReffDoc').value;
        if (current && !this.refDocList.includes(current)) {
          this.refDocList.push(current);
        }
        this.loadingRefDocs = false;
      },
      error: () => { this.loadingRefDocs = false; }
    });
  }

  addRefDoc() {
    const newValue = this.newRefDoc && this.newRefDoc.trim();
    if (!newValue || this.refDocList.includes(newValue)) return;
    const clinicId = this.ls.GID || this.ls.ClinicID;
    this.loadingRefDocs = true;
    this.gs.addDropdownOption(clinicId, 'ReffDoc', newValue).subscribe({
      next: () => {
        this.refDocList.push(newValue);
        this.gs.gastroForm.get('ReffDoc').setValue(newValue);
        this.newRefDoc = '';
        this.loadingRefDocs = false;
      },
      error: () => { this.loadingRefDocs = false; }
    });
  }

  deleteRefDoc(value: string) {
    const clinicId = this.ls.GID || this.ls.ClinicID;
    this.gs.deleteDropdownOption(clinicId, 'ReffDoc', value).subscribe({
      next: () => {
        this.refDocList = this.refDocList.filter(v => v !== value);
        if (this.gs.gastroForm.get('ReffDoc').value === value) {
          this.gs.gastroForm.get('ReffDoc').setValue('');
        }
      },
      error: () => {}
    });
  }

  deleteProbeType(value: string) {
    const clinicId = this.ls.GID || this.ls.ClinicID;
    this.gs.deleteDropdownOption(clinicId, 'ProbeType', value).subscribe({
      next: () => {
        this.probeTypeDropdownList = this.probeTypeDropdownList.filter(v => v !== value);
        if (this.gs.gastroForm.get('ProbeType').value === value) {
          this.gs.gastroForm.get('ProbeType').setValue('');
        }
      },
      error: () => {}
    });
  }

  addProbeType() {
    const newValue = this.extraProbeType && this.extraProbeType.trim();
    if (!newValue || this.probeTypeDropdownList.includes(newValue)) return;
    this.loadingProbeTypes = true;
    // Use clinicId from login service (GID)
    const clinicId = this.ls.GID || this.ls.ClinicID;
    this.gs.addDropdownOption(clinicId, 'ProbeType', newValue).subscribe({
      next: () => {
        this.probeTypeDropdownList.push(newValue);
        this.gs.gastroForm.get('ProbeType').setValue(newValue);
        this.extraProbeType = '';
        this.loadingProbeTypes = false;
      },
      error: () => {
        // Optionally show error
        this.loadingProbeTypes = false;
      }
    });
  }

  ngOnInit(): void {
    this.loadRefDocs();
    // Load Probe Type options from backend
    const clinicId = this.ls.GID || this.ls.ClinicID;
    this.loadingProbeTypes = true;
    this.gs.getDropdownOptions(clinicId, 'ProbeType').subscribe({
      next: (options: string[]) => {
        this.probeTypeDropdownList = options || [];
        // Ensure ProbeType is a string (single selection)
        let probeTypeValue = this.gs.gastroForm.get('ProbeType').value;
        if (Array.isArray(probeTypeValue)) {
          probeTypeValue = probeTypeValue.length > 0 ? probeTypeValue[0] : '';
          this.gs.gastroForm.get('ProbeType').setValue(probeTypeValue, { emitEvent: false });
        }
        if (probeTypeValue && !this.probeTypeDropdownList.includes(probeTypeValue)) {
          this.probeTypeDropdownList.push(probeTypeValue);
        }
        this.loadingProbeTypes = false;
      },
      error: () => {
        this.loadingProbeTypes = false;
      }
    });
  }
}
